import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AdminTicket } from "@sd/types/admin";
import { assigneeDisplayName } from "@sd/lib/adminTicketUtils";
import {
  mapRawToAdminTicket,
  looksLikeOpaqueUserId,
} from "@sd/lib/ticketRowMapping";
import { resolvePriorityDisplay } from "@sd/lib/priorityListingUtils";
import { getUserDisplayFromMap } from "@sd/lib/userDisplayMap";
import { buildAttachmentsFromTicketRecord } from "@sd/lib/parseAttachmentIds";
import { useAttachmentFilesByIds } from "@sd/hooks/useAttachmentFilesByIds";
import { normalizeTicketCreateDetailPayload } from "@sd/lib/ticketCreateDetail";
import {
  fetchTicketDetailHistory,
  parseTicketDetailHistoryEntries,
} from "@sd/lib/fetchTicketDetailHistory";
import {
  parseTicketNotesResponse,
  type TicketNoteEntry,
} from "@sd/lib/ticketNotesApi";
import { useServiceDeskUserDisplayMap } from "@sd/hooks/useServiceDeskUserDisplayMap";
import ServiceDeskAPIService, {
  serviceDeskTicketCreateDetailQueryKey,
  serviceDeskTicketHistoryQueryKey,
  serviceDeskTicketNotesQueryKey,
} from "@sd/services/APIs";

function resolveUserLabel(
  raw: string | undefined | null,
  userDisplayMap: Map<string, string>,
  agentEmployees: { id: string; name: string }[]
): string {
  if (!raw) return "—";
  const t = String(raw).trim();
  if (!t) return "—";
  if (looksLikeOpaqueUserId(t)) {
    return (
      getUserDisplayFromMap(userDisplayMap, t) ??
      agentEmployees.find(a => a.id === t)?.name ??
      t
    );
  }
  return t;
}

function pickCreatedByFromRaw(raw: unknown): string {
  if (!raw || typeof raw !== "object") return "";
  const r = raw as Record<string, unknown>;
  return String(
    r.created_by_name ??
      r.created_by_display ??
      r.created_by ??
      r.createdBy ??
      ""
  ).trim();
}

function pickDisplayNameFromRaw(
  raw: Record<string, unknown>,
  keys: string[]
): string {
  for (const key of keys) {
    const value = String(raw[key] ?? "").trim();
    if (value && !looksLikeOpaqueUserId(value)) {
      return value;
    }
  }
  return "";
}

export function useTicketDetailPage(ticketId: string | undefined) {
  const { userDisplayMap, agentEmployees, priorityIdToLabel } =
    useServiceDeskUserDisplayMap({ refetchAgentListsOnMount: false });

  const {
    data: detailPayload,
    isPending,
    isError,
  } = useQuery({
    queryKey: serviceDeskTicketCreateDetailQueryKey(ticketId),
    queryFn: async () => {
      const res = await ServiceDeskAPIService.tickets.getCreatedTicket(ticketId!);
      if (res.data === undefined || res.data === null) {
        throw new Error("Ticket detail failed");
      }
      return normalizeTicketCreateDetailPayload(res.data);
    },
    enabled: Boolean(ticketId),
    staleTime: 15_000,
    retry: 1,
  });

  const {
    data: historyResult,
    isPending: historyPending,
    isError: historyError,
  } = useQuery({
    queryKey: serviceDeskTicketHistoryQueryKey(ticketId),
    queryFn: () => fetchTicketDetailHistory(ticketId!),
    enabled: Boolean(ticketId),
    staleTime: 30_000,
    retry: 1,
  });

  const {
    data: notesRaw,
    isPending: notesPending,
    isError: notesError,
  } = useQuery({
    queryKey: serviceDeskTicketNotesQueryKey(ticketId),
    queryFn: async () => {
      const res = await ServiceDeskAPIService.ticketNotes.list(
        { ticket_id: ticketId!, page: 1, page_size: 200 },
        { skipErrorNotification: true }
      );
      return res.data;
    },
    enabled: Boolean(ticketId),
    staleTime: 15_000,
    retry: 1,
  });

  const notes = useMemo((): TicketNoteEntry[] => {
    return parseTicketNotesResponse(notesRaw);
  }, [notesRaw]);

  const ticket = useMemo((): AdminTicket | null => {
    if (!ticketId || !detailPayload) return null;
    return mapRawToAdminTicket(detailPayload);
  }, [ticketId, detailPayload]);

  const stubAttachments = useMemo(() => {
    if (!detailPayload) return [];
    return buildAttachmentsFromTicketRecord(detailPayload);
  }, [detailPayload]);

  const attachmentIds = useMemo(
    () => stubAttachments.map(a => a.id),
    [stubAttachments]
  );

  const { attachments: hydratedAttachments, loading: attachmentsHydrating } =
    useAttachmentFilesByIds(attachmentIds.length ? attachmentIds : undefined);

  const attachments = useMemo(() => {
    if (!stubAttachments.length) return [];
    const hydratedById = new Map(hydratedAttachments.map(a => [a.id, a]));
    return stubAttachments.map(
      stub => hydratedById.get(stub.id) ?? stub
    );
  }, [stubAttachments, hydratedAttachments]);

  const activityEntries = useMemo(() => {
    return parseTicketDetailHistoryEntries(historyResult, ticket, detailPayload);
  }, [historyResult, ticket, detailPayload]);

  const resolveUser = useMemo(() => {
    return (raw: string | undefined | null) =>
      resolveUserLabel(raw, userDisplayMap, agentEmployees);
  }, [userDisplayMap, agentEmployees]);

  const display = useMemo(() => {
    if (!ticket || !detailPayload) {
      return {
        requesterName: "—",
        assigneeName: "—",
        priorityLabel: "—",
        createdByName: "—",
      };
    }

    const requesterFromApi = pickDisplayNameFromRaw(detailPayload, [
      "requester_name",
      "requester_display_name",
      "requester_full_name",
    ]);
    const assigneeFromApi = pickDisplayNameFromRaw(detailPayload, [
      "assigned_name",
      "assignee_name",
      "assigned_to_name",
    ]);
    const createdByRaw =
      pickCreatedByFromRaw(detailPayload) || ticket.requester;
    const createdByName = resolveUser(createdByRaw);
    const requesterName = requesterFromApi || resolveUser(ticket.requester);

    return {
      requesterName: requesterName !== "—" ? requesterName : createdByName,
      assigneeName:
        assigneeFromApi ||
        assigneeDisplayName(ticket, agentEmployees, userDisplayMap),
      priorityLabel: resolvePriorityDisplay(
        ticket.priorityId,
        ticket.priority,
        priorityIdToLabel
      ),
      createdByName:
        createdByName !== "—" ? createdByName : requesterName,
    };
  }, [
    ticket,
    detailPayload,
    agentEmployees,
    priorityIdToLabel,
    resolveUser,
  ]);

  return {
    ticket,
    display,
    attachments,
    attachmentIds,
    attachmentsLoading: attachmentIds.length > 0 && attachmentsHydrating,
    activityEntries,
    activityPending: Boolean(ticketId) && historyPending,
    activityError: historyError && !historyResult,
    notes,
    notesPending: Boolean(ticketId) && notesPending,
    notesError: notesError && !notesRaw,
    resolveUserLabel: resolveUser,
    isLoading: Boolean(ticketId) && isPending,
    isError: isError && !ticket,
  };
}
